import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { AddRounded, LockRounded } from "@mui/icons-material";
import {
  colors,
  fonts,
  glassChipSx,
  gradients,
  radii,
  shadows,
} from "../../@shared/ui/tokens";

const pressableSx = {
  transition:
    "transform 150ms ease, background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
  "&:hover": { transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0) scale(0.97)" },
  "&.Mui-focusVisible": {
    outline: `2px solid ${colors.gold}`,
    outlineOffset: 2,
  },
};

function CountPill({ count, dot = false, sx }) {
  if (dot) {
    return (
      <Box
        component="span"
        aria-hidden
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: gradients.play,
          boxShadow: "0 0 0 2px #0d0720",
          ...sx,
        }}
      />
    );
  }
  if (!count) return null;
  return (
    <Box
      component="span"
      sx={{
        minWidth: 20,
        height: 20,
        px: 0.6,
        borderRadius: `${radii.pill}px`,
        background: gradients.play,
        color: "#fff",
        fontSize: "0.68rem",
        fontWeight: 800,
        lineHeight: "20px",
        textAlign: "center",
        boxShadow: "0 0 0 2px #0d0720, 0 4px 10px rgba(255,77,109,0.4)",
        ...sx,
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
}

// Top-bar currency readout (tokens, wallet).
export function HudCurrency({ icon, value, label, onClick, showAdd, dataDriver, compact, dot }) {
  return (
    <ButtonBase
      data-driver={dataDriver}
      onClick={onClick}
      aria-label={label}
      sx={{
        ...glassChipSx,
        ...pressableSx,
        height: compact ? 34 : 40,
        pl: compact ? 0.5 : 0.75,
        pr: showAdd ? 0.5 : compact ? 1.1 : 1.5,
        position: "relative",
        gap: compact ? 0.6 : 1,
        borderRadius: `${radii.pill}px`,
        "&:hover": { ...pressableSx["&:hover"], borderColor: "rgba(255,211,106,0.5)" },
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        sx={{
          width: compact ? 24 : 30,
          height: compact ? 24 : 30,
          objectFit: "contain",
          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
        }}
      />
      <Typography
        component="span"
        sx={{
          fontFamily: fonts.display,
          fontSize: compact ? "0.92rem" : "1.1rem",
          color: colors.goldBright,
          lineHeight: 1,
          minWidth: compact ? 0 : 28,
          textAlign: "left",
          letterSpacing: "0.02em",
        }}
      >
        {value}
      </Typography>
      {showAdd && (
        <Box
          component="span"
          sx={{
            width: compact ? 24 : 28,
            height: compact ? 24 : 28,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background: gradients.gold,
            color: "#3a1f00",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
            "& svg": { fontSize: compact ? 18 : 20 },
          }}
        >
          <AddRounded />
        </Box>
      )}
      {dot ? <CountPill dot sx={{ top: -2, right: -2 }} /> : null}
    </ButtonBase>
  );
}

// Round top-bar icon button with an optional count.
export function HudIconButton({ icon, label, onClick, count, dot, dataDriver, size = 42, children }) {
  return (
    <ButtonBase
      data-driver={dataDriver}
      onClick={onClick}
      aria-label={label}
      title={label}
      sx={{
        ...glassChipSx,
        ...pressableSx,
        position: "relative",
        width: size,
        height: size,
        borderRadius: `${radii.md}px`,
        flexShrink: 0,
        color: colors.text,
        "& svg": { fontSize: size * 0.55 },
        "&:hover": { ...pressableSx["&:hover"], backgroundColor: colors.surfaceHover },
      }}
    >
      {typeof icon === "string" ? (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{
            width: size * 0.7,
            height: size * 0.7,
            objectFit: "contain",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
          }}
        />
      ) : (
        icon
      )}
      {children}
      {count ? (
        <CountPill count={count} sx={{ position: "absolute", top: -6, right: -6 }} />
      ) : dot ? (
        <CountPill dot />
      ) : null}
    </ButtonBase>
  );
}

// Player identity plate: level emblem + name + xp bar.
export function PlayerPlate({ badge, level, name, xpPercent, onClick, dataDriver, compact }) {
  return (
    <ButtonBase
      data-driver={dataDriver}
      onClick={onClick}
      aria-label={`Level ${level}`}
      sx={{
        ...glassChipSx,
        ...pressableSx,
        height: compact ? 40 : 48,
        pl: 0.5,
        pr: compact ? 1.25 : 2,
        gap: 1,
        borderRadius: `${radii.pill}px`,
        justifyContent: "flex-start",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: compact ? 32 : 40,
          height: compact ? 32 : 40,
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          background: "radial-gradient(circle at 50% 35%, #4a2fb8 0%, #1a0d44 100%)",
          boxShadow: `0 0 0 2px ${colors.gold}, ${shadows.glowGold}`,
        }}
      >
        <Box
          component="img"
          src={badge}
          alt=""
          sx={{ width: "78%", height: "78%", objectFit: "contain" }}
        />
      </Box>
      <Box sx={{ minWidth: 0, textAlign: "left" }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontFamily: fonts.display,
            fontSize: compact ? "0.9rem" : "1.05rem",
            lineHeight: 1.05,
            color: colors.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: compact ? 92 : 160,
          }}
        >
          {name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}>
          <Typography
            component="span"
            sx={{
              fontSize: "0.62rem",
              fontWeight: 800,
              letterSpacing: "0.12em",
              color: colors.gold,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            LV {level}
          </Typography>
          {!compact && xpPercent != null && (
            <Box
              sx={{
                width: 72,
                height: 5,
                borderRadius: radii.pill,
                background: "rgba(255,255,255,0.12)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${Math.max(4, Math.min(100, xpPercent))}%`,
                  height: "100%",
                  borderRadius: radii.pill,
                  background: gradients.gold,
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </ButtonBase>
  );
}

// Grouped vertical dock of menu destinations.
export function HudDock({ title, children, side = "left", compact }) {
  return (
    <Box
      component="nav"
      aria-label={title}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: compact ? 0.75 : 0.5,
        p: compact ? 0 : 0.75,
        borderRadius: `${radii.lg}px`,
        ...(compact
          ? {}
          : {
              background:
                side === "left"
                  ? "linear-gradient(90deg, rgba(10,5,24,0.78) 0%, rgba(10,5,24,0.5) 100%)"
                  : "linear-gradient(270deg, rgba(10,5,24,0.78) 0%, rgba(10,5,24,0.5) 100%)",
              border: `1px solid ${colors.line}`,
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }),
      }}
    >
      {children}
    </Box>
  );
}

export function HudDockItem({
  icon,
  label,
  onClick,
  count,
  dot,
  locked,
  dataDriver,
  compact,
  side = "left",
}) {
  const iconBox = (
    <Box
      sx={{
        position: "relative",
        width: compact ? 44 : 38,
        height: compact ? 44 : 38,
        flexShrink: 0,
        borderRadius: `${radii.md}px`,
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, rgba(124,92,255,0.45) 0%, rgba(59,31,158,0.55) 100%)",
        border: "1px solid rgba(157,133,255,0.45)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
        ...(compact ? { ...glassChipSx, background: "linear-gradient(180deg, rgba(124,92,255,0.55) 0%, rgba(40,20,110,0.75) 100%)" } : {}),
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        sx={{
          width: "76%",
          height: "76%",
          objectFit: "contain",
          filter: locked
            ? "grayscale(0.9) brightness(0.6) drop-shadow(0 2px 3px rgba(0,0,0,0.5))"
            : "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
        }}
      />
      {locked && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            color: colors.goldBright,
            "& svg": { fontSize: 18, filter: "drop-shadow(0 1px 2px #000)" },
          }}
        >
          <LockRounded />
        </Box>
      )}
      {compact && count ? (
        <CountPill count={count} sx={{ position: "absolute", top: -7, right: -7 }} />
      ) : compact && dot ? (
        <CountPill dot sx={{ top: -3, right: -3 }} />
      ) : null}
    </Box>
  );

  if (compact) {
    return (
      <ButtonBase
        data-driver={dataDriver}
        onClick={onClick}
        aria-label={label}
        sx={{
          ...pressableSx,
          flexDirection: "column",
          gap: 0.35,
          borderRadius: `${radii.md}px`,
          opacity: locked ? 0.75 : 1,
        }}
      >
        {iconBox}
        <Typography
          component="span"
          sx={{
            fontSize: "0.56rem",
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: colors.text,
            textShadow: "0 1px 3px rgba(0,0,0,0.9)",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <ButtonBase
      data-driver={dataDriver}
      onClick={onClick}
      aria-label={label}
      sx={{
        position: "relative",
        width: "100%",
        height: 48,
        px: 0.75,
        gap: 1.25,
        justifyContent: "flex-start",
        flexDirection: side === "right" ? "row-reverse" : "row",
        borderRadius: `${radii.md}px`,
        color: locked ? colors.textMuted : colors.text,
        transition: "background-color 150ms ease, transform 150ms ease",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 10,
          bottom: 10,
          [side === "right" ? "right" : "left"]: -6,
          width: 3,
          borderRadius: 3,
          background: gradients.gold,
          opacity: 0,
          transition: "opacity 150ms ease",
        },
        "&:hover": {
          backgroundColor: "rgba(124, 92, 255, 0.2)",
          transform: side === "right" ? "translateX(-2px)" : "translateX(2px)",
        },
        "&:hover::before": { opacity: 1 },
        "&.Mui-focusVisible": {
          outline: `2px solid ${colors.gold}`,
          outlineOffset: 1,
        },
      }}
    >
      {iconBox}
      <Typography
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: side === "right" ? "right" : "left",
          fontWeight: 800,
          fontSize: "0.82rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          textShadow: "0 1px 3px rgba(0,0,0,0.6)",
        }}
      >
        {label}
      </Typography>
      {count ? <CountPill count={count} /> : dot ? <CountPill dot sx={{ position: "static" }} /> : null}
    </ButtonBase>
  );
}

// Primary call to action (PLAY / CARDS).
export function HudCta({ label, sublabel, onClick, variant = "play", dataDriver, icon, compact }) {
  const isPlay = variant === "play";
  return (
    <ButtonBase
      data-driver={dataDriver}
      onClick={onClick}
      aria-label={label}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: compact ? 58 : isPlay ? 78 : 66,
        px: compact ? 1.5 : 2.5,
        gap: 1.5,
        borderRadius: `${radii.lg}px`,
        justifyContent: "center",
        background: isPlay ? gradients.play : gradients.violet,
        border: `2px solid ${isPlay ? "rgba(255,200,210,0.65)" : "rgba(200,190,255,0.55)"}`,
        boxShadow: isPlay
          ? `${shadows.glowPlay}, inset 0 2px 0 rgba(255,255,255,0.45), inset 0 -4px 0 rgba(0,0,0,0.22)`
          : `0 10px 26px rgba(80,50,220,0.45), inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -4px 0 rgba(0,0,0,0.22)`,
        animation: isPlay ? "infinft-pulse-ring 2.4s ease-out infinite" : "none",
        transition: "transform 150ms ease, filter 150ms ease",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "40%",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 100%)",
          animation: "infinft-shimmer 4.5s ease-in-out infinite",
          pointerEvents: "none",
        },
        "&:hover": { transform: "translateY(-2px)", filter: "brightness(1.08)" },
        "&:active": { transform: "translateY(0) scale(0.98)" },
        "&.Mui-focusVisible": {
          outline: `3px solid ${colors.gold}`,
          outlineOffset: 3,
        },
      }}
    >
      {icon && (
        <Box sx={{ display: "grid", placeItems: "center", color: "#fff", "& svg": { fontSize: compact ? 26 : 34, filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.35))" } }}>
          {icon}
        </Box>
      )}
      <Box sx={{ textAlign: "left", lineHeight: 1 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontFamily: fonts.display,
            fontSize: compact
              ? "1.45rem"
              : isPlay
                ? { xs: "1.6rem", sm: "2.2rem" }
                : { xs: "1.35rem", sm: "1.7rem" },
            lineHeight: 1,
            whiteSpace: "nowrap",
            letterSpacing: "0.04em",
            color: "#fff",
            textShadow: "0 2px 0 rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.3)",
          }}
        >
          {label}
        </Typography>
        {sublabel && !compact && (
          <Typography
            component="span"
            sx={{
              display: "block",
              mt: 0.4,
              fontSize: "0.66rem",
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)",
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

// Small labelled slot next to the avatar (badge sticker, loadout).
export function LoadoutSlot({ label, image, onClick, accent = colors.violet, compact }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      sx={{
        ...pressableSx,
        flexDirection: "column",
        gap: 0.5,
        borderRadius: `${radii.md}px`,
      }}
    >
      <Box
        sx={{
          ...glassChipSx,
          width: compact ? 50 : 64,
          height: compact ? 50 : 64,
          borderRadius: `${radii.md}px`,
          display: "grid",
          placeItems: "center",
          borderColor: `${accent}88`,
          boxShadow: `0 0 18px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.15)`,
        }}
      >
        <Box
          component="img"
          src={image}
          alt=""
          sx={{
            width: "78%",
            height: "78%",
            objectFit: "contain",
            filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.5))",
          }}
        />
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: compact ? "0.56rem" : "0.62rem",
          fontWeight: 800,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: colors.text,
          textShadow: "0 1px 3px rgba(0,0,0,0.9)",
          maxWidth: compact ? 64 : 84,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

// Game-mode row used by the PLAY menu.
export function ModeTile({ title, description, icon, accent = colors.violet, onClick, dataDriver, featured }) {
  return (
    <ButtonBase
      data-driver={dataDriver}
      onClick={onClick}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        minHeight: featured ? 76 : 64,
        px: 1.5,
        py: 1,
        gap: 1.5,
        justifyContent: "flex-start",
        borderRadius: `${radii.md + 2}px`,
        textAlign: "left",
        color: colors.text,
        background: `linear-gradient(100deg, ${accent}40 0%, ${accent}14 45%, rgba(255,255,255,0.03) 100%)`,
        border: `1px solid ${accent}66`,
        boxShadow: featured ? `0 0 24px ${accent}44, inset 0 1px 0 rgba(255,255,255,0.12)` : "inset 0 1px 0 rgba(255,255,255,0.08)",
        transition: "transform 150ms ease, border-color 150ms ease, background 150ms ease",
        "&:hover": {
          transform: "translateX(3px)",
          borderColor: accent,
          background: `linear-gradient(100deg, ${accent}5c 0%, ${accent}22 50%, rgba(255,255,255,0.05) 100%)`,
        },
        "&:hover .mode-chevron": { transform: "translateX(3px)", opacity: 1 },
        "&.Mui-focusVisible": { outline: `2px solid ${colors.gold}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          width: featured ? 50 : 44,
          height: featured ? 50 : 44,
          flexShrink: 0,
          borderRadius: `${radii.md}px`,
          display: "grid",
          placeItems: "center",
          color: "#fff",
          background: `linear-gradient(180deg, ${accent} 0%, ${accent}aa 100%)`,
          boxShadow: `0 6px 16px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.4)`,
          "& svg": { fontSize: featured ? 28 : 24 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontFamily: fonts.display,
            fontSize: featured ? "1.35rem" : "1.15rem",
            lineHeight: 1.1,
            letterSpacing: "0.02em",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            component="span"
            sx={{
              display: "block",
              mt: 0.25,
              fontSize: "0.74rem",
              color: colors.textSoft,
              lineHeight: 1.3,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Box
        className="mode-chevron"
        component="span"
        sx={{
          color: colors.textSoft,
          opacity: 0.6,
          fontSize: "1.4rem",
          lineHeight: 1,
          transition: "transform 150ms ease, opacity 150ms ease",
        }}
      >
        ›
      </Box>
    </ButtonBase>
  );
}
